import DashboardLayout from "@/layout/dashboard-layout";
import { ReactNode, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, CheckCircle, XCircle, Info, Clock, Trash2, Check } from "lucide-react";

type Alert = {
  id: number;
  type: "warning" | "error" | "success" | "info";
  title: string;
  message: string;
  blog: string;
  created_at: string;
  read: boolean;
  priority: "high" | "medium" | "low";
};

type AlertsStats = {
  total: number;
  unread: number;
  high_priority: number;
  medium_priority: number;
  low_priority: number;
};

type AlertsIndexProps = {
  alerts: Alert[];
  stats: AlertsStats;
};

const AlertsIndex = (props: AlertsIndexProps) => {
  const { alerts, stats } = props;
  const [filter, setFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");

  const filteredAlerts = alerts.filter(alert => {
    const matchesType = filter === "all" || 
                       (filter === "unread" && !alert.read) ||
                       (filter === "read" && alert.read) ||
                       alert.type === filter;
    const matchesPriority = priorityFilter === "all" || alert.priority === priorityFilter;
    return matchesType && matchesPriority;
  });

  const getAlertIcon = (type: string) => {
    switch (type) {
      case "error":
        return <XCircle className="h-5 w-5 text-red-500" />;
      case "warning":
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case "success":
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case "info":
        return <Info className="h-5 w-5 text-blue-500" />;
      default:
        return <Info className="h-5 w-5 text-gray-500" />;
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case "high":
        return <Badge variant="destructive">Alta</Badge>;
      case "medium":
        return <Badge variant="default">Média</Badge>;
      case "low":
        return <Badge variant="secondary">Baixa</Badge>;
      default:
        return <Badge variant="secondary">{priority}</Badge>;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case "error":
        return "Erro";
      case "warning":
        return "Aviso";
      case "success":
        return "Sucesso";
      case "info":
        return "Informação";
      default:
        return type;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Central de Alertas</h1>
          <p className="text-muted-foreground">
            Monitore eventos importantes da sua rede de blogs
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline">
            <Check className="mr-2 h-4 w-4" />
            Marcar Todos como Lidos
          </Button>
        </div>
      </div>

      {/* Estatísticas */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground">Todos os alertas</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Não Lidos</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{stats.unread}</div>
            <p className="text-xs text-muted-foreground">Precisam de atenção</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Alta Prioridade</CardTitle>
            <XCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{stats.high_priority}</div>
            <p className="text-xs text-muted-foreground">Urgentes</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Média Prioridade</CardTitle>
            <AlertTriangle className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{stats.medium_priority}</div>
            <p className="text-xs text-muted-foreground">Moderados</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Baixa Prioridade</CardTitle>
            <Info className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{stats.low_priority}</div>
            <p className="text-xs text-muted-foreground">Informativos</p>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Alertas</CardTitle>
          <CardDescription>
            Visualize e gerencie todos os alertas do sistema
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-4 mb-6">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filtrar por tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="unread">Não Lidos</SelectItem>
                <SelectItem value="read">Lidos</SelectItem>
                <SelectItem value="error">Erros</SelectItem>
                <SelectItem value="warning">Avisos</SelectItem>
                <SelectItem value="success">Sucessos</SelectItem>
                <SelectItem value="info">Informações</SelectItem>
              </SelectContent>
            </Select>

            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filtrar por prioridade" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as Prioridades</SelectItem>
                <SelectItem value="high">Alta Prioridade</SelectItem>
                <SelectItem value="medium">Média Prioridade</SelectItem>
                <SelectItem value="low">Baixa Prioridade</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-4">
            {filteredAlerts.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="mx-auto h-12 w-12 text-green-500" />
                <h3 className="mt-4 text-lg font-semibold">Nenhum alerta encontrado</h3>
                <p className="text-muted-foreground">
                  {filter === "all" 
                    ? "Não há alertas no momento. Tudo está funcionando perfeitamente!"
                    : "Nenhum alerta corresponde aos filtros selecionados."
                  }
                </p>
              </div>
            ) : (
              filteredAlerts
                .sort((a, b) => {
                  // Ordenar por: não lidos primeiro, depois por prioridade, depois por data
                  if (a.read !== b.read) return a.read ? 1 : -1;
                  
                  const priorityOrder = { high: 3, medium: 2, low: 1 };
                  if (a.priority !== b.priority) {
                    return priorityOrder[b.priority] - priorityOrder[a.priority];
                  }
                  
                  return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
                })
                .map((alert) => (
                  <Card key={alert.id} className={`${!alert.read ? 'border-l-4 border-l-blue-500 bg-blue-50/50' : ''}`}>
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-4 flex-1">
                          <div className="mt-1">
                            {getAlertIcon(alert.type)}
                          </div>
                          <div className="flex-1 space-y-2">
                            <div className="flex items-center space-x-2">
                              <h4 className="font-semibold">{alert.title}</h4>
                              {!alert.read && (
                                <Badge variant="outline" className="text-xs">
                                  Novo
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {alert.message}
                            </p>
                            <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                              <span>Blog: {alert.blog}</span>
                              <span>•</span>
                              <span>Tipo: {getTypeLabel(alert.type)}</span>
                              <span>•</span>
                              <span>{new Date(alert.created_at).toLocaleString('pt-BR')}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          {getPriorityBadge(alert.priority)}
                          <Button variant="ghost" size="sm" title="Marcar como lido">
                            <Check className="h-3 w-3" />
                          </Button>
                          <Button variant="ghost" size="sm" title="Excluir alerta">
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

AlertsIndex.layout = (page: ReactNode) => (
  <DashboardLayout>{page}</DashboardLayout>
);

export default AlertsIndex;
